/* global fetch, Headers, window Response */
import "whatwg-fetch";

const EventSource = window.EventSource,
    ObjectToString = Object.prototype.toString,
    // slice = Array.prototype.slice,
    createObject = Object.create || function create(from) {
      /**
       * Empty prototype function
       */
      function T() {}
      T.prototype = from;
      return new T();
    },

    noop = () => {},

    /**
     * A very naive and simplistic implementation of extending 'classes'
     * @param   {Function|Object} From        The Constructor function to extend
     * @param   {Object}          extraProps The additional properties and methods to add to
     *                                          the resulting object.
     * @return {Object}          The newly created extended object
     */
    extend = (From, extraProps) => {
      // we provide for initialization after constructor call
      const {_initialize: initialize = noop} = extraProps;
      // once initialized, we don't really need it in the actual object
      delete extraProps._initialize;

      /**
       * The extended object
       * @param  {...any} args The arguments to "sub class"
       */
      function F(...args) {
        From.apply && From.apply(this, args);
        // call subclass initialization
        initialize.apply(this, args);
      }

      const Proto = F.prototype = createObject(From.prototype);
      Object.keys(extraProps).forEach(k => Proto[k] = extraProps[k]);
      Proto.constructor = F;
      return F;
    },

    /**
     * Returns whether the specified object is an array
     * @param {Any} that any argument whose arrayness needs to be checkd
     * @return {Boolean} true if 'that' is an array
     */
    isArray = that => {
      return ObjectToString.call(that) === "[object Array]";
    },

    EventEmitterProto = {
      on(event, handler) {
        const handlers = this.event_handlers[event] || (this.event_handlers[event] = []);
        handlers.push(handler);
        return {
          dispose() {
            const index = handlers.indexOf(handler);
            if(index !== -1) {
              handlers.splice(index, 1);
            }
          }
        };
      },
      once(event, handler) {
        const subs = this.on(event, (...args) => {
          subs.dispose();
          handler(event, ...args);
        });
      },
      emit(event, ...args) {
        const handlers = this.event_handlers[event] || [];
        handlers.forEach(h => {
          h(event, ...args);
        });
      }
    },
    createEventEmitter = () => {
      return Object.create(EventEmitterProto, {
        event_handlers: {
          value: {}
        }
      });
    },
    ChannelTypes = {};



/**
 * A channel represents a data channel in the datalayer that can be subscribed to. It exposes an
 * observable object via its observale() method and data is published to subscribers whenever its
 * available Channels are created by DataLayer via its create(config) method. Application code
 * can create various channels in datalayer.
 *
 * @param {String} name   The name of this channel.
 * @param {Object} config The channel configuration object
 */
function Channel(name, config) {
  this.name = name;
  this.config = config;
  this.event_emitter = createEventEmitter();
}
Channel.prototype = {
  constructor: Channel,

  open: function() {
    this.event_emitter.emit("open");
  },

  update: function(config) {},

  close: function() {
    this.event_emitter.emit("close");
  },

  on: function(event, listener) {
    return this.event_emitter.on(event, listener);
  },

  emit: function(event, data) {
    // console.log("[Channel] Emitting", event, data);
    this.event_emitter.emit(event, data);
  }
};



/**
 * Define a channel for a specified type.
 * @param {Object} implementation The implementation of the channel
 * @return {Channel} The newly defined channel type
 */
Channel.define = function(implementation) {
  let impl = implementation;
  if(typeof implementation === "function") {
    impl = implementation();
  }
  return extend(Channel, impl);
};

/**
 * Registers a channel for a specified type.
 * @param {String} type The type of the channel to define. Can be any string e.g. "ws", "sse", etc.
 * @param {Object} implementation The implementation of the channel
 * @return {String} The type
 */
Channel.register = function(type, implementation) {
  const Type = Channel.define(implementation);
  ChannelTypes[type] = Type;
  return Type;
};

Channel.configureDefaults = function(type, config) {
  if(!ChannelTypes[type]) {
    console.debug("Channel type " + type + " not known");
    return;
  }
  ChannelTypes[type].defaults = Object.assign({}, ChannelTypes[type].defaults || {}, config);
};

Channel.getByType = function(type) {
  return ChannelTypes[type];
};

Channel.create = function(name, implementation, config = {}) {
  const ChannelType = Channel.define(implementation);
  return new ChannelType(name, config);
};


/**
 * A channel to HTTP(S) protocol. The following configuration parameters are supported
 * {
 *   url: "http://graphite/render",         // The URL to fetch from
 *   method: "GET",                         // HTTP method usually, GET or POST, defaults to GET
 *   data: {some: "data"},  // Data to be sent with the request
 *   params: {
 *     target: "averageSeries(server.web*.load)",
 *     from: "-30min",
 *     format: "json"
 *   },
 *   headers: {
 *
 *   },
 *   interval: 1000                         // Interval to make this http request
 * }
 */
Channel.register("http", function() {
  const makeHeaders = (opts = {}) => {
        return Object.keys(opts).reduce((hdrs, k) => {
          hdrs.append(k, opts[k]);
          return hdrs;
        }, new Headers());
      },

      /**
       * Builds a parameter array with each component as key=value from the specified object
       * @param {Object} objParams The parameters as object
       * @return {Array} The array representation of query parameters that can be then
       *                 joined by join("&")
       */
      asQueryParameters = (objParams = {}) => {
        if(!objParams) {
          return [];
        }

        return Object.keys(objParams).reduce((a, k) => {
          const value = objParams[k];
          if(isArray(value)) {
            value.forEach(v => a.push(k + "=" + encodeURIComponent(v)));
          }else {
            a.push(k + "=" + encodeURIComponent(value));
          }
          return a;
        }, []);
      },

      getResponsePromise = (res, resType) => {
        let content;
        switch (resType) {
          case "json":
            content = res.json();
            break;
          case "text":
            content = res.text();
            break;
          default:
            content = res.text();
            break;
        }
        return content;
      };

  return {
    open() {
      this.emit("open");
      this.params = asQueryParameters(this.config.params).join("&");
      this.on("fetchstart", () => this.fetching = true);
      this.on("fetchend", () => this.fetching = false);
      this.start();
    },

    start() {
      // first time, fetch immediately
      this.fetch();
      let interval = Number(this.config.interval);
      if(!isNaN(interval)) {
        interval = interval < 1000 ? 1000 : interval;
        this.tickId = setInterval(() => {
          if(this.fetching) {
            // ongoing fetch
            console.debug("Still fetching previous request...");
            return;
          }
          this.fetch();
        }, interval);
      }
    },

    fetch(fetchParams) {
      this.emit("fetchstart", url);
      const config = this.config,
          params = fetchParams || this.params,
          // If thie is a FQ URL then this is a CORS request
          xdr = config.url.indexOf("http://") === 0 || config.url.indexOf("https://") === 0,
          // if params are supplied then append them as querystring
          url = config.url + (params ? "?" + params : ""),
          reqOptions = config.requestOptions || {},
          headers = makeHeaders(config.headers),
          accept = config.responseType || "text",
          fetchRequest = Object.assign({}, reqOptions, {
            method: config.method || "GET",
            mode: xdr ? "cors" : "same-origin",
            headers: headers,
            body: config.data
          });

      // Make the http request
      fetch(url, fetchRequest)
        .then(res => {
          this.emit("fetchend", url);
          if(res.status >= 200 && res.status < 400) {
            return getResponsePromise(res, accept);
          }else {
            return Promise.reject(res);
          }
        })
        .then(content => {
          // console.log(content);
          this.emit("data", content);
        })
        .catch(err => {
          this.emit("fetchend", url);
          if(err instanceof Response) {
            err.text().then(body => {
              this.emit("error", {
                url: err.url,
                status: err.status,
                statusText: err.statusText,
                content: body
              });
            });
          }else {
            this.emit("error", err);
          }
        });
    },

    update(config) {
      if(config) {
        this.config = Object.assign({}, config);
        this.params = asQueryParameters(config.params).join("&");
      }
      clearInterval(this.tickId);
      this.emit("update");
      this.start();
    },

    close() {
      clearInterval(this.tickId);
      this.emit("close");
    }
  };
});



/**
 * A channel to SSE (Server Sent Events)
 * {
 *   url: "http://server.com/sse",
 *   events: "diskusage ramusage cpuusage",
 *   dataType: "text"
 * }
 */
Channel.register("sse", function() {
  const createEventSource = (channel, config) => {
    const url = config.url,
        source = new EventSource(url),
        events = config.events || "message",
        listeners = {
          open(e) {
            channel.emit("open", e);
          },
          error(e) {
            channel.emit("error", e);
          },
          close() {
            // source.close();
            channel.emit("close", null);
          }
        };

    source.addEventListener("open", listeners.open);
    source.addEventListener("error", listeners.error);
    source.addEventListener("close", listeners.close);

    events.split(" ").forEach(type => {
      if(!listeners[type]) {
        const listener = listeners[type] = e => {
          channel.handleSSE(type, e);
        };
        source.addEventListener(type, listener);
      }
    });
    source.__dlListeners = listeners;
    return source;
  };

  return {
    open() {
      this.source = createEventSource(this, this.config);
    },

    handleSSE(type, event) {
      const {name: cName} = this;
      this.emit("data", {
        name: cName,
        type: type,
        data: event.data
      });
    },

    update(config) {
      if(!config) {
        return;
      }
      const listeners = this.source.__dlListeners;
      Object.keys(listeners).forEach(type => {
        this.source.removeEventListener(type, listeners[type]);
      });
      this.source.close();
      this.config = Object.assign({}, config);
      this.open();
      this.emit("update");
    },

    close() {
      this.source && this.source.close();
    }
  };
});



// module.exports = Channel;
export default Channel;
